/** Subscription and context usage, read the same way everywhere: window names, the pressure ramp that
 *  colours a usage meter, and reset times. Pure helpers, so shared UI (`RailMeter`), feature screens
 *  and charts all draw from this one module. */

import { formatCost, providerUsageAfterRead, usagePressure, usageWindowLabel } from './displayFormat';
import { interpolate } from './i18n/interpolate';
import type { LocaleDict } from './i18n/types';
import type { BrainProvider, ProviderUsage, RoutedUsageKey, UsageWindow } from './types';

/** The ramp addressed at a `Progress` fill. Spelled out per branch rather than composed from a colour
 *  name because Tailwind scans class names statically — an interpolated `bg-${tone}` would never be
 *  emitted into the stylesheet. */
export function usageProgressClass(pct: number): string {
  switch (usagePressure(pct)) {
    case 'critical': return 'bg-destructive';
    case 'warning': return 'bg-warning';
    default: return 'bg-primary';
  }
}

/** The same ramp as a CSS colour, for a meter the charting library draws rather than a class does: an
 *  SVG fill cannot take a Tailwind utility. Both readings come off `usagePressure`, so a bar and a chart
 *  can never disagree about where amber begins. */
export function usageProgressColour(pct: number): string {
  switch (usagePressure(pct)) {
    case 'critical': return 'var(--color-destructive)';
    case 'warning': return 'var(--color-warning)';
    default: return 'var(--color-primary)';
  }
}

/** A tiny non-zero usage still shows a sliver, so a meter that is barely used never reads as untouched. */
export function usageMeterValue(pct: number): number {
  const clamped = Math.max(0, Math.min(100, pct));
  return clamped > 0 ? Math.max(clamped, 3) : 0;
}

/** When a subscription window resets, in the reader's locale; empty when the account did not say. */
function usageResetLabel(resetsAt: number | null, locale: string): string {
  if (resetsAt == null || !Number.isFinite(resetsAt)) return '';
  const at = new Date(resetsAt * 1_000);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleString(locale, { weekday: 'short', hour: '2-digit', minute: '2-digit' });
}

/** The windows a meter list draws. An unlimited `spend` window says only that the account has no limit, which is
 *  noise beside the provider's own windows, so it is left out there; alone it stays and reads "unlimited". A spend
 *  window with a limit always draws. The data keeps it either way: the account drawer offers its limit slider from it. */
export function drawnUsageWindows(windows: readonly UsageWindow[]): UsageWindow[] {
  const unlimitedSpend = (window: UsageWindow) => window.kind === 'spend' && window.amount.total === null;
  return windows.some((window) => !unlimitedSpend(window)) ? windows.filter((window) => !unlimitedSpend(window)) : [...windows];
}

/** One representative reading for a Settings card; drawers keep the original full usage. */
export function cardUsage(usage: ProviderUsage | null): ProviderUsage | null {
  if (!usage) return null;
  const timed = usage.windows.filter((window) => window.kind !== 'spend' && window.windowMinutes !== null);
  const selected = timed.find((window) => window.windowMinutes === 10_080)
    ?? timed.reduce<UsageWindow | undefined>((longest, window) => !longest || window.windowMinutes! > longest.windowMinutes! ? window : longest, undefined)
    ?? usage.windows.find((window) => window.kind === 'spend')
    ?? usage.windows[0];
  return selected ? { ...usage, windows: [selected] } : null;
}

/** Where a routed provider's combined figures are filed in `GET /brain/rate-limits/all`. The daemon's
 *  `routedUsageKey` writes this key and `isRoutedUsageProvider` decides who has one; the browser cannot import
 *  daemon code, so the key is spelled once here for every rail. */
export function routedUsageKey(providerId: string): RoutedUsageKey {
  return `routing:${providerId}`;
}

/** One reading a provider's allowance draws, named by the account it belongs to when the provider has several. */
export interface ProviderUsageReading { key: string; label: string | null; usage: ProviderUsage }

/** The readings one provider has, taken from the ONE map `GET /brain/rate-limits/all` serves — the same entries
 *  its records draw, so a row and the statistics drawer can never disagree about them. A routed provider over
 *  several accounts is its combined entry alone; every other provider draws one reading per account that
 *  reported something. A failed read withdraws figures past the shared freshness rule
 *  (`providerUsageAfterRead`), and a provider with nothing left keeps the "not reported" line. */
export function providerUsageReadings(
  provider: Pick<BrainProvider, 'id' | 'routing' | 'connections'>,
  entries: Readonly<Record<string, ProviderUsage>> | undefined,
  failed: boolean,
  now: number,
): ProviderUsageReading[] {
  const reading = (key: string, label: string | null): ProviderUsageReading[] => {
    const usage = providerUsageAfterRead(entries?.[key], failed, now);
    return usage ? [{ key, label, usage }] : [];
  };
  if (provider.routing.enabled && provider.connections.length > 1) return reading(routedUsageKey(provider.id), null);
  const named = provider.connections.length > 1;
  return provider.connections.flatMap((connection) => reading(connection.id, named ? connection.label : null));
}

/** How one usage window reads on a meter: its label, the figure on the right edge (the rounded percentage
 *  when absent) and the hover hint. The ONE mapping from the shared `UsageWindow` shape, so the Settings
 *  account row, the chat rail's Limits section and its compact strip name a window the same way. A window
 *  without a `kind` is a subscription time window; the kinded ones are money or request meters whose
 *  figure is what is LEFT (a balance is never printed as a total), and the account's own spend, whose figure
 *  is what was spent of its limit (or that it has none). */
export function usageWindowMeter(
  window: UsageWindow,
  dict: LocaleDict,
  locale: string,
): { label: string; value?: string; title?: string } {
  const t = dict.brain;
  const reset = usageResetLabel(window.resetsAt, locale);
  const resets = reset ? interpolate(t.usageResets, { time: reset }) : '';
  // What is left of a money meter, never below zero (an overspent key reads as empty, not negative).
  const left = (amount: { used: number; total: number }) => formatCost(Math.max(0, amount.total - amount.used), 2);
  switch (window.kind) {
    case 'credits': {
      const remaining = left(window.amount);
      return { label: t.usageBalance, value: remaining, title: interpolate(t.usageBalanceHint, { amount: remaining }) };
    }
    case 'limit': {
      const period = window.windowMinutes === 1_440 ? t.usageDaily
        : window.windowMinutes === 10_080 ? t.usageWeekly
        : window.windowMinutes === 43_200 ? t.usageMonthly
        : '';
      const cap = interpolate(t.usageKeyLimitHint, { amount: formatCost(window.amount.total, 2) });
      return { label: t.usageLimit, value: left(window.amount), title: [cap, period, resets].filter(Boolean).join(', ') };
    }
    case 'requests':
      return {
        label: t.usageFreeModels,
        value: `${window.amount.used}/${window.amount.total}`,
        title: reset ? interpolate(t.usageResetsUtc, { time: reset }) : undefined,
      };
    // No limit: the bar stays empty and says so. The figures are the rail's fixed-width money chips, like the
    // balance above; a limit resets with the month, so its hint is a date.
    case 'spend':
      return window.amount.total === null
        ? { label: t.usageSpend, value: dict.spendLimit.unlimited }
        : {
          label: t.usageSpend,
          value: `${formatCost(window.amount.used, 2)} / ${formatCost(window.amount.total, 2)}`,
          title: window.resetsAt === null ? undefined
            : interpolate(t.usageResets, { time: new Date(window.resetsAt * 1_000).toLocaleDateString(locale) }),
        };
    default:
      return { label: usageWindowLabel(window.windowMinutes, t.usageWeekly, t.usageWindow), title: resets || undefined };
  }
}
